import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, redirect, useNavigate, Link } from '@tanstack/react-router'
import { usePropertyMatches } from '../hooks/useMatches'
import {
  useBulkSegnala,
  useCreateProposal,
  useMarkProposalSent,
} from '../hooks/useProposals'
import { hasTokens } from '../lib/auth'
import type { MatchForPropertyRow } from '../lib/api'
import { formatShortCode } from '../lib/shortCode'

type MatchSearch = {
  limit: number
  min_score: number
}

export const Route = createFileRoute('/lotti/$id/match')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): MatchSearch => {
    const toNum = (v: unknown, dflt: number, min: number, max: number): number => {
      const n = typeof v === 'number' ? v : Number(v)
      if (!Number.isFinite(n)) return dflt
      return Math.min(max, Math.max(min, n))
    }
    return {
      limit: toNum(search.limit, 50, 1, 500),
      min_score: toNum(search.min_score, 40, 0, 100),
    }
  },
  component: PropertyMatchPage,
})

const currencyFmt = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

function formatCurrency(v: number | null): string {
  if (v === null || v === undefined) return ''
  return currencyFmt.format(v)
}

function scoreColor(score: number): string {
  if (score >= 70) return '#1a8a3a'
  if (score >= 50) return '#cc7a00'
  return '#777'
}

function breakdownLabel(b: MatchForPropertyRow['breakdown']): string {
  const parts: string[] = []
  if (b.budget_fit > 0) parts.push(`B${Math.round(b.budget_fit)}`)
  if (b.zone_match > 0) parts.push(`Z+${Math.round(b.zone_match)}`)
  if (b.semantic > 0) parts.push(`S+${Math.round(b.semantic)}`)
  if (b.rating_bonus > 0) parts.push(`R+${Math.round(b.rating_bonus)}`)
  return parts.length ? `${parts.join(' ')} = ${b.total}` : `= ${b.total}`
}

function PropertyMatchPage() {
  const { id } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/lotti/$id/match' })
  const [minScoreLocal, setMinScoreLocal] = useState<string>(String(search.min_score))
  const [pendingClientId, setPendingClientId] = useState<string | null>(null)
  const [errorClientId, setErrorClientId] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')

  // Filtri client-side (zona + budget) sui match già caricati.
  const [zonaFilter, setZonaFilter] = useState<string>('')
  const [budgetMin, setBudgetMin] = useState<string>('')
  const [budgetMax, setBudgetMax] = useState<string>('')

  // Selezione multipla + bulk segnala.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [segnalatiIds, setSegnalatiIds] = useState<Set<string>>(new Set())
  const [bulkMsg, setBulkMsg] = useState<string>('')

  useEffect(() => {
    setMinScoreLocal(String(search.min_score))
  }, [search.min_score])

  const { data, isPending, isError, error } = usePropertyMatches(id, {
    limit: search.limit,
    min_score: search.min_score,
  })

  const createProposal = useCreateProposal()
  const markSent = useMarkProposalSent()
  const bulkSegnalaMut = useBulkSegnala()

  const filtered = useMemo(() => {
    const items = data?.items ?? []
    const z = zonaFilter.trim().toUpperCase()
    const bMin = budgetMin.trim() ? Number(budgetMin) : null
    const bMax = budgetMax.trim() ? Number(budgetMax) : null
    return items.filter((m) => {
      if (z) {
        const zones = (m.client.zone_tag ?? []).map((s) => s.toUpperCase())
        if (!zones.some((zt) => zt.includes(z))) return false
      }
      const b = m.client.budget
      if (bMin != null && Number.isFinite(bMin) && (b == null || b < bMin)) return false
      if (bMax != null && Number.isFinite(bMax) && (b == null || b > bMax)) return false
      return true
    })
  }, [data, zonaFilter, budgetMin, budgetMax])

  const filteredIds = useMemo(() => filtered.map((m) => m.client.id), [filtered])
  const allSelected =
    filteredIds.length > 0 && filteredIds.every((cid) => selected.has(cid))

  function toggleOne(clientId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(clientId)) next.delete(clientId)
      else next.add(clientId)
      return next
    })
  }

  function toggleAll() {
    setSelected((prev) => {
      if (filteredIds.every((cid) => prev.has(cid))) {
        // tutti i filtrati già selezionati → deseleziona solo quelli
        const next = new Set(prev)
        filteredIds.forEach((cid) => next.delete(cid))
        return next
      }
      return new Set([...prev, ...filteredIds])
    })
  }

  function applyMinScore() {
    const n = Math.min(100, Math.max(0, Math.round(Number(minScoreLocal) || 0)))
    void navigate({
      search: ((s: MatchSearch) => ({
        ...s,
        min_score: n,
      })) as never,
    })
  }

  async function onBulkSegnala(): Promise<void> {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    setBulkMsg('')
    try {
      const res = await bulkSegnalaMut.mutateAsync({
        property_id: id,
        client_ids: ids,
      })
      setSegnalatiIds((prev) => new Set([...prev, ...ids]))
      setSelected(new Set())
      const extra: string[] = []
      if (res.skipped_already_sent > 0)
        extra.push(`${res.skipped_already_sent} già inviati`)
      if (res.skipped_forbidden > 0)
        extra.push(`${res.skipped_forbidden} non tuoi`)
      setBulkMsg(
        `📌 Segnalati ${res.segnalato_total} clienti ` +
          `(${res.created} nuovi, ${res.updated} aggiornati` +
          (extra.length ? `; saltati: ${extra.join(', ')}` : '') +
          ')',
      )
    } catch (err) {
      setBulkMsg(
        err instanceof Error ? err.message : 'Errore segnalazione bulk',
      )
    }
  }

  async function onSendWhatsApp(m: MatchForPropertyRow): Promise<void> {
    setErrorClientId(null)
    setErrorMsg('')
    setPendingClientId(m.client.id)
    try {
      const proposal = await createProposal.mutateAsync({
        property_id: id,
        client_id: m.client.id,
        channel: 'whatsapp',
      })
      if (!proposal.wa_url) {
        setErrorClientId(m.client.id)
        setErrorMsg('Telefono mancante')
        return
      }
      window.open(proposal.wa_url, '_blank', 'noopener,noreferrer')
      try {
        await markSent.mutateAsync(proposal.id)
      } catch {
        // Non-fatal — message was opened, sent flag is best-effort.
      }
    } catch (err) {
      setErrorClientId(m.client.id)
      setErrorMsg(err instanceof Error ? err.message : 'Errore invio proposta')
    } finally {
      setPendingClientId(null)
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Match per Lotto</h1>
        <span className="muted">Lotto ID: {id}</span>
      </div>

      <div className="filters-row" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <label
          className="muted"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          Score minimo:
          <input
            type="number"
            min={0}
            max={100}
            value={minScoreLocal}
            onChange={(e) => setMinScoreLocal(e.target.value)}
            className="filter-input"
            style={{ maxWidth: '6rem' }}
          />
          <button className="secondary" onClick={applyMinScore}>
            Applica
          </button>
        </label>

        <label
          className="muted"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          Zona:
          <input
            type="text"
            value={zonaFilter}
            onChange={(e) => setZonaFilter(e.target.value)}
            placeholder="tag zona cliente"
            className="filter-input"
            style={{ maxWidth: '10rem' }}
          />
        </label>

        <label
          className="muted"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          Budget:
          <input
            type="number"
            value={budgetMin}
            onChange={(e) => setBudgetMin(e.target.value)}
            placeholder="min"
            className="filter-input"
            style={{ maxWidth: '8rem' }}
          />
          <span>–</span>
          <input
            type="number"
            value={budgetMax}
            onChange={(e) => setBudgetMax(e.target.value)}
            placeholder="max"
            className="filter-input"
            style={{ maxWidth: '8rem' }}
          />
        </label>

        {data && (
          <span className="muted">
            {filtered.length} di {data.total_matches} match · {data.total_candidates}{' '}
            candidati
          </span>
        )}
      </div>

      {/* Barra azioni bulk segnala */}
      <div
        className="filters-row"
        style={{ alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}
      >
        <button
          className="secondary"
          disabled={selected.size === 0 || bulkSegnalaMut.isPending}
          onClick={() => void onBulkSegnala()}
          title="Segnala questo lotto ai clienti selezionati (senza invio)"
        >
          {bulkSegnalaMut.isPending
            ? 'Segnalo…'
            : `📌 Segnala a ${selected.size} selezionati`}
        </button>
        {bulkMsg && <span className="muted">{bulkMsg}</span>}
      </div>

      {isPending && <div className="muted">Caricamento…</div>}
      {isError && (
        <div className="error">
          {error?.message ?? 'Errore nel caricamento match'}
        </div>
      )}

      {data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    title="Seleziona tutti (filtrati)"
                  />
                </th>
                <th>Score</th>
                <th>📱 WhatsApp</th>
                <th>Code</th>
                <th>Cognome Nome</th>
                <th>Priorità</th>
                <th>Rating</th>
                <th>Tipologia</th>
                <th>Budget</th>
                <th>Zone</th>
                <th>Consulente</th>
                <th>Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.client.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(m.client.id)}
                      onChange={() => toggleOne(m.client.id)}
                    />
                  </td>
                  <td>
                    <strong style={{ color: scoreColor(m.score) }}>
                      {m.score}
                    </strong>
                  </td>
                  <td>
                    <button
                      className="secondary"
                      disabled={pendingClientId === m.client.id}
                      onClick={() => void onSendWhatsApp(m)}
                      title={`${m.client.cognome} ${m.client.nome}`.slice(0, 80)}
                    >
                      {pendingClientId === m.client.id ? '…' : 'Invia'}
                    </button>
                    {errorClientId === m.client.id && (
                      <div
                        className="error"
                        style={{ fontSize: '0.75em', marginTop: '0.25rem' }}
                      >
                        {errorMsg}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="text-xs font-mono text-slate-500">
                      {formatShortCode('client', m.client.short_code)}
                    </span>
                  </td>
                  <td>
                    <Link
                      to="/clienti/$id"
                      params={{ id: m.client.id }}
                      search={{} as never}
                      className="link-cliente"
                      title="Apri scheda cliente"
                    >
                      <strong>{m.client.cognome}</strong> {m.client.nome}
                    </Link>
                    {(() => {
                      // Mostra il badge per QUALSIASI proposal esistente (non
                      // solo 'segnalato'): se il cliente è già stato segnalato/
                      // inviato, persiste al refresh dal proposal_status server.
                      const st = segnalatiIds.has(m.client.id)
                        ? 'segnalato'
                        : m.proposal_status
                      if (!st) return null
                      const label =
                        st === 'segnalato'
                          ? '📌 segnalato'
                          : st === 'rejected'
                            ? '🚫 rifiutato'
                            : st === 'won'
                              ? '🏆 vinto'
                              : st === 'lost'
                                ? '❌ perso'
                                : '✓ inviato'
                      return (
                        <span
                          className="tag"
                          style={{ marginLeft: 6, fontSize: '0.7em' }}
                          title={`Stato proposta: ${st}`}
                        >
                          {label}
                        </span>
                      )
                    })()}
                  </td>
                  <td>{m.client.priorita}</td>
                  <td>{m.client.rating ?? ''}</td>
                  <td>{m.client.tipologia ?? ''}</td>
                  <td className="cell-num">{formatCurrency(m.client.budget)}</td>
                  <td className="cell-truncate">
                    {(m.client.zone_tag ?? []).join(', ')}
                  </td>
                  <td>{m.client.consultant_name ?? ''}</td>
                  <td>
                    <span className="muted" style={{ fontSize: '0.85em' }}>
                      {breakdownLabel(m.breakdown)}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={12} className="muted" style={{ textAlign: 'center' }}>
                    Nessun match {data.items.length > 0 ? 'coi filtri attuali' : ''} (score
                    ≥ {search.min_score})
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
